import type { ReactNode } from "react";
import clsx from "clsx";
import { Check } from "lucide-react";

import underlineStyles from "./underline-fields.module.css";
import styles from "./wizard-rail-layout.module.css";

export type WizardRailStep = {
  id: string;
  label: string;
};

export type WizardRailLayoutProps = {
  steps: WizardRailStep[];
  currentStepIndex: number;
  sectionTitle: string;
  children: ReactNode;
  onBack?: () => void;
  primaryLabel?: string;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  primaryFormId?: string;
  onPrimary?: () => void;
};

export function WizardRailLayout({
  steps,
  currentStepIndex,
  sectionTitle,
  children,
  onBack,
  primaryLabel = "Continue",
  primaryDisabled = false,
  primaryLoading = false,
  primaryFormId,
  onPrimary,
}: WizardRailLayoutProps) {
  const stepCount = steps.length;
  const isFirst = currentStepIndex <= 0;
  const showBack = !isFirst && onBack != null;

  const progressPct =
    stepCount <= 1 ? 100 : (currentStepIndex / (stepCount - 1)) * 100;

  const handlePrimary = () => {
    if (primaryFormId) {
      const form = document.getElementById(primaryFormId);
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
        return;
      }
    }
    onPrimary?.();
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.rail} aria-label="Form progress">
        <div className={styles.railTrack}>
          <ol className={styles.railSteps}>
            {steps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isDone = index < currentStepIndex;

              return (
                <li
                  key={step.id}
                  className={clsx(
                    styles.railStep,
                    isActive && styles.railStepActive,
                    isDone && styles.railStepDone,
                  )}
                  aria-current={isActive ? "step" : undefined}
                >
                  <span className={styles.stepMarker} aria-hidden>
                    {isDone ? (
                      <Check size={15} strokeWidth={2.5} />
                    ) : (
                      <span className={styles.stepMarkerNum}>{index + 1}</span>
                    )}
                  </span>
                  <span className={styles.stepCopy}>
                    <span className={styles.stepEyebrow}>Step {index + 1}</span>
                    <span className={styles.stepLabel}>{step.label}</span>
                  </span>
                  {isActive ? (
                    <span className={styles.activePin} aria-hidden />
                  ) : null}
                </li>
              );
            })}
          </ol>
          <div className={styles.progressTrack} aria-hidden>
            <div
              className={styles.progressFill}
              style={{
                height: `${progressPct}%`,
                ["--progress-width" as string]: `${progressPct}%`,
              }}
            />
          </div>
        </div>
      </aside>

      <section className={styles.card}>
        <h2 className={styles.cardHeader}>{sectionTitle}</h2>
        <div className={clsx(styles.cardBody, underlineStyles.fieldsRoot)}>{children}</div>
        <footer className={styles.cardFooter}>
          {showBack ? (
            <button
              type="button"
              className={styles.backBtn}
              disabled={primaryLoading}
              onClick={onBack}
            >
              Back
            </button>
          ) : (
            <span className={styles.footerSpacer} aria-hidden />
          )}
          <button
            type="button"
            className={styles.primary}
            disabled={primaryDisabled || primaryLoading}
            onClick={handlePrimary}
          >
            {primaryLoading ? "Please wait…" : primaryLabel}
          </button>
        </footer>
      </section>
    </div>
  );
}
